/**
 * What the translation pill in `RecipeHeader` should offer.
 *
 * Its own module, and pure, because the rule is not obvious and getting it
 * wrong is invisible: the pill is the ONLY control that moves between the
 * translation and the original, in either direction. It used to render on
 * `isTranslated` alone — a flag the original recipe does not carry — so tapping
 * it hid the pill and stranded the reader in a language they had not asked for,
 * with no way back short of leaving the screen.
 *
 * The rule: offer the way back whenever the screen is showing the original,
 * unconditionally. Offer the way out only when there is a translation on screen
 * AND a language worth naming on the pill.
 */

export type TranslationPillAction =
  | { action: 'show-original'; language: 'en' | 'fr' }
  | { action: 'show-translation' };

export function translationPill(input: {
  isTranslating?: boolean;
  isTranslated?: boolean;
  showingOriginal?: boolean;
  originalLanguage?: 'en' | 'fr' | 'unknown';
}): TranslationPillAction | null {
  // Mid-flight the header shows a "Traduction…" label instead; two competing
  // affordances in the same slot is worse than none.
  if (input.isTranslating) return null;

  // Deliberately first, and deliberately not conditional on anything else.
  // Whatever else is true, someone reading the original must be able to get
  // back — including when the language could not be identified.
  if (input.showingOriginal) return { action: 'show-translation' };

  if (!input.isTranslated) return null;
  if (!input.originalLanguage || input.originalLanguage === 'unknown') return null;

  return { action: 'show-original', language: input.originalLanguage };
}
